<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      pendingFiles: [],
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.pendingFiles = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    triggerAttach() {
      this.$refs.attachInput?.click();
    },
    onSelectFile(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;

      this.pendingFiles.push({
        file,
        name: file.name,
        isImage: file.type.startsWith('image/'),
        url: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      });
    },
    removePendingFile(index) {
      this.pendingFiles.splice(index, 1);
    },
    async uploadPendingAttachments(id) {
      await Promise.all(
        this.pendingFiles.map(item =>
          this.$store.dispatch('attachCannedMedia', { id, file: item.file })
        )
      );
    },
    async addCannedResponse() {
      this.addCanned.showLoading = true;
      try {
        const canned = await this.$store.dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
        });
        await this.uploadPendingAttachments(canned.id);
        this.addCanned.showLoading = false;
        useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.resetForm();
        this.onClose();
      } catch (error) {
        this.addCanned.showLoading = false;
        const errorMessage =
          error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <div class="w-full mt-2">
          <label>{{ $t('CANNED_MGMT.ATTACH.LABEL') }}</label>
          <div v-if="pendingFiles.length" class="flex flex-wrap gap-2 mt-1">
            <div
              v-for="(item, index) in pendingFiles"
              :key="`${item.name}-${index}`"
              class="relative flex items-center gap-2 max-w-56 py-1.5 pl-2 pr-8 rounded-md border border-slate-100 dark:border-slate-700 bg-slate-25 dark:bg-slate-800"
            >
              <img
                v-if="item.isImage"
                :src="item.url"
                :alt="item.name"
                class="object-cover rounded size-8"
              />
              <fluent-icon
                v-else
                icon="document"
                class="text-slate-600 dark:text-slate-200"
              />
              <span
                class="text-sm truncate text-slate-700 dark:text-slate-100"
                :title="item.name"
              >
                {{ item.name }}
              </span>
              <woot-button
                v-tooltip.top="$t('CANNED_MGMT.ATTACH.REMOVE')"
                variant="clear"
                color-scheme="alert"
                size="tiny"
                icon="dismiss-circle"
                class="absolute right-0.5 top-0.5"
                @click="removePendingFile(index)"
              />
            </div>
          </div>
          <NextButton
            faded
            slate
            sm
            icon="i-lucide-paperclip"
            type="button"
            class="mt-2"
            :label="$t('CANNED_MGMT.ATTACH.BUTTON_TEXT')"
            @click.prevent="triggerAttach"
          />
          <input
            ref="attachInput"
            type="file"
            class="hidden"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            @change="onSelectFile"
          />
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
